import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { languageLocaleMap, supportedLanguageCodes } from "../translations/defaultTranslations";

const SITE_NAME = "Velaris Royal Car Rental";
const SITE_URL = (process.env.REACT_APP_SITE_URL || "https://velaris-royal.com").replace(/\/+$/, "");
const DEFAULT_IMAGE = `${SITE_URL}/velaris-logo-full.png`;

export const SEO = ({
  title,
  description,
  keywords,
  image,
  type = "website",
  noindex = false,
  structuredData,
  breadcrumbs,
  children,
}) => {
  const location = useLocation();
  const { currentLanguage, isRTL } = useLanguage();
  const canonicalUrl = `${SITE_URL}${location.pathname}`;
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const ogImage = image || DEFAULT_IMAGE;
  const lang = currentLanguage || "en";
  const dir = isRTL ? "rtl" : "ltr";
  const ogLocale = languageLocaleMap[lang] || languageLocaleMap.en;

  const breadcrumbLD = breadcrumbs
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((item, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: item.name,
          item: item.url ? `${SITE_URL}${item.url}` : undefined,
        })),
      }
    : null;

  return (
    <Helmet>
      <html lang={lang} dir={dir} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:locale" content={ogLocale} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Hreflang */}
      {supportedLanguageCodes.map((code) => (
        <link
          key={code}
          rel="alternate"
          hreflang={code}
          href={code === "en" ? `${SITE_URL}${location.pathname}` : `${SITE_URL}${location.pathname}?lang=${code}`}
        />
      ))}
      <link rel="alternate" hreflang="x-default" href={`${SITE_URL}${location.pathname}`} />

      {/* Structured Data - Organization (always present) */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AutoRental",
          name: SITE_NAME,
          url: SITE_URL,
          logo: DEFAULT_IMAGE,
          description: "Premium luxury car rental services in Dubai, Abu Dhabi and across the UAE.",
          address: {
            "@type": "PostalAddress",
            addressLocality: "Dubai",
            addressRegion: "Dubai",
            addressCountry: "AE",
          },
          geo: {
            "@type": "GeoCoordinates",
            latitude: 25.2048,
            longitude: 55.2708,
          },
          telephone: process.env.REACT_APP_DEFAULT_PHONE || undefined,
          priceRange: "Luxury rental rates available daily",
          areaServed: [
            { "@type": "City", name: "Dubai" },
            { "@type": "City", name: "Abu Dhabi" },
            { "@type": "City", name: "Sharjah" },
            { "@type": "City", name: "Ajman" },
          ],
          openingHoursSpecification: {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            opens: "00:00",
            closes: "23:59",
          },
        })}
      </script>

      {/* Breadcrumbs */}
      {breadcrumbLD && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbLD)}
        </script>
      )}

      {/* Page-specific structured data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}

      {children}
    </Helmet>
  );
};

// Helper: Build vehicle structured data
export const buildVehicleLD = (car) => ({
  "@context": "https://schema.org",
  "@type": "Car",
  name: `${car.brand} ${car.model} ${car.year}`,
  brand: { "@type": "Brand", name: car.brand },
  model: car.model,
  vehicleModelDate: String(car.year),
  numberOfDoors: car.doors,
  seatingCapacity: car.seats,
  fuelType: car.fuelType,
  vehicleTransmission: car.transmission,
  description: car.description,
  image: car.image,
  offers: {
    "@type": "Offer",
    priceCurrency: car.currency || "AED",
    price: car.pricePerDay,
    availability: "https://schema.org/InStock",
    priceValidUntil: new Date(Date.now() + 30 * 86400000).toISOString().split("T")[0],
    seller: {
      "@type": "AutoRental",
      name: SITE_NAME,
    },
  },
});

// Helper: Build FAQ structured data
export const buildFAQLD = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
});

export default SEO;
